import { Box } from '@twilio-paste/core/box';
import {
  FormControl,
  FormControlTwoColumn,
  FormSection,
  FormSectionHeading,
} from '@twilio-paste/core/form';
import { HelpText } from '@twilio-paste/core/help-text';
import { Input } from '@twilio-paste/core/input';
import { Separator } from '@twilio-paste/core/separator';
import { Stack } from '@twilio-paste/core/stack';
import { TextArea } from '@twilio-paste/core/textarea';
import { useUIDSeed } from '@twilio-paste/core/uid-library';
import { Field, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { CreateScopeParams } from '../../../modules/Apps/Scopes';
import { InfoLabel } from '../../common/InfoLabel';
import { Switch } from '../../common/Switch';

export interface ScopesFormValues {
  appId: string;
  name: string;
  keyword: string;
  byPassOptIn: boolean;
  doubleOptIn: boolean;
  doubleOptInMsg: string;
  doubleOptInAcceptOptions: string;
  doubleOptInRejectOptions: string;
}

export const ScopesFormValuesToDto = (values: ScopesFormValues): CreateScopeParams => {
  return { ...values };
};

export const createScopesFormValidationSchema = () => {
  return Yup.object().shape({
    appId: Yup.string().max(26, 'App id is too long').required('App is required'),
    name: Yup.string().max(100, 'Scope name is too long').required('Scope name is required'),
    keyword: Yup.string().max(100, 'Keyword is too long'),
    byPassOptIn: Yup.boolean(),
    doubleOptIn: Yup.boolean(),
    doubleOptInMsg: Yup.string()
      .when('doubleOptIn', {
        is: true,
        then: schema => schema.required('Double Opt In Message is required.'),
      })
      .max(1600, 'Double Opt In Message is too long'),
    doubleOptInAcceptOptions: Yup.string().max(1000, 'Double Opt In Accept Options is too long'),
    doubleOptInRejectOptions: Yup.string().max(1000, 'Double Opt In Reject Options is too long'),
  });
};

interface ScopesFormProps {
  readonly?: boolean;
  isEditing?: boolean;
}

export function ScopesForm({ readonly, isEditing }: ScopesFormProps) {
  const seed = useUIDSeed();
  const { touched, errors, values } = useFormikContext<ScopesFormValues>();

  return (
    <Box maxWidth="600px">
      <Stack orientation="vertical" spacing="space60">
        <FormSection>
          <FormSectionHeading>Scope Details</FormSectionHeading>
          <FormControl>
            <InfoLabel
              htmlFor={seed('name')}
              infoText="Please provide a friendly name for this app scope."
              required
            >
              Scope Name
            </InfoLabel>

            <Field
              name="name"
              as={Input}
              type="text"
              id={seed('name')}
              placeholder="Scope Name"
              hasError={touched.name && !!errors.name}
              readOnly={readonly}
            />
            {touched.name && !!errors.name && <HelpText variant="error">{errors.name}</HelpText>}
          </FormControl>
        </FormSection>
        <Separator orientation="horizontal" />
        <FormSection>
          <FormSectionHeading>SMS Configuration</FormSectionHeading>
          <FormControl>
            <InfoLabel
              htmlFor={seed('keyword')}
              infoText="Please provide a keyword to trigger this app scope. Please note that the keyword is case-insentative. "
              required
            >
              Keyword
            </InfoLabel>
            <Field
              name="keyword"
              as={Input}
              type="text"
              id={seed('keyword')}
              placeholder="Keyword"
              hasError={touched.keyword && !!errors.keyword}
              readOnly={readonly || isEditing}
            />
            {touched.keyword && !!errors.keyword && (
              <HelpText variant="error">{errors.keyword}</HelpText>
            )}
          </FormControl>

          <FormControlTwoColumn>
            <FormControl>
              <Switch
                name="byPassOptIn"
                id={seed('byPassOptIn')}
                helpText="Bypass opt in"
                hasError={touched.byPassOptIn && !!errors.byPassOptIn}
                disabled={readonly || values?.doubleOptIn}
              >
                <InfoLabel
                  htmlFor={seed('byPassOptIn')}
                  infoText="Enable this if you want to bypass sending an opt-in message to customers when they send an inbound message to the app."
                >
                  Bypass Opt-In
                </InfoLabel>
              </Switch>
              {touched.byPassOptIn && !!errors.byPassOptIn && (
                <HelpText variant="error">{errors.byPassOptIn}</HelpText>
              )}
            </FormControl>

            <FormControl>
              <Switch
                name="doubleOptIn"
                id={seed('doubleOptIn')}
                helpText="Double opt in"
                hasError={touched.doubleOptIn && !!errors.doubleOptIn}
                disabled={readonly || values?.byPassOptIn}
              >
                <InfoLabel
                  htmlFor={seed('doubleOptIn')}
                  infoText="Enable this if you want to send customers a double opt-in message before routing inbound messages to another service."
                >
                  Double Opt-In
                </InfoLabel>
              </Switch>
              {touched.doubleOptIn && !!errors.doubleOptIn && (
                <HelpText variant="error">{errors.doubleOptIn}</HelpText>
              )}
            </FormControl>
          </FormControlTwoColumn>

          {values.doubleOptIn && (
            <Box display="flex" flexDirection="column" rowGap="space60">
              <FormControl>
                <InfoLabel
                  htmlFor={seed('doubleOptInMsg')}
                  infoText="Please provide the double opt-in message to send to customers."
                  required
                >
                  Double Opt-In Message
                </InfoLabel>
                <Field
                  name="doubleOptInMsg"
                  as={TextArea}
                  maxLength={1600}
                  type="text"
                  id={seed('doubleOptInMsg')}
                  placeholder="Double Opt In Message"
                  hasError={touched.doubleOptInMsg && !!errors.doubleOptInMsg}
                  disabled={readonly}
                />
                {touched.doubleOptInMsg && !!errors.doubleOptInMsg && (
                  <HelpText variant="error">{errors.doubleOptInMsg}</HelpText>
                )}
              </FormControl>
              <FormControl>
                <InfoLabel
                  htmlFor={seed('doubleOptInAcceptOptions')}
                  infoText="Please provide a list of comma-separated words customers can use to accept the double opt-in. "
                  required
                >
                  Double Opt-In Acceptance Options
                </InfoLabel>
                <Field
                  name="doubleOptInAcceptOptions"
                  as={Input}
                  type="text"
                  id={seed('doubleOptInAcceptOptions')}
                  placeholder="Double Opt In Accept Options"
                  hasError={touched.doubleOptInAcceptOptions && !!errors.doubleOptInAcceptOptions}
                  disabled={readonly}
                />
                {touched.doubleOptInAcceptOptions && !!errors.doubleOptInAcceptOptions && (
                  <HelpText variant="error">{errors.doubleOptInAcceptOptions}</HelpText>
                )}
              </FormControl>
              <FormControl>
                <InfoLabel
                  htmlFor={seed('doubleOptInRejectOptions')}
                  infoText="Please provide a list of comma-separated words customers can use to reject the double opt-in."
                  required
                >
                  Double Opt-In Rejection Options
                </InfoLabel>
                <Field
                  name="doubleOptInRejectOptions"
                  as={Input}
                  type="text"
                  id={seed('doubleOptInRejectOptions')}
                  placeholder="Double Opt In Reject Options"
                  hasError={touched.doubleOptInRejectOptions && !!errors.doubleOptInRejectOptions}
                  disabled={readonly}
                />
                {touched.doubleOptInRejectOptions && !!errors.doubleOptInRejectOptions && (
                  <HelpText variant="error">{errors.doubleOptInRejectOptions}</HelpText>
                )}
              </FormControl>
            </Box>
          )}
        </FormSection>
      </Stack>
    </Box>
  );
}
